/**
 * PilotageConcentrationController — page `/concentration` (EPIC-031 /
 * 31.4, spec §16.B).
 *
 * Charge `GET /pilotage/concentration` au `connect()` et hydrate :
 *  - la bannière d'alerte (titre, message, mention BNC, disclaimer
 *    URSSAF) — masquée si `maxPalier` est `null` (aucun client ≥ 30 %) ;
 *  - le `<tbody>` du tableau Répartition du CA (un `<tr>` par client +
 *    une ligne « + N autres » si plus de 5 clients + une ligne total) ;
 *  - le timeline des franchissements (10 derniers `pilotage_event`) ;
 *  - le camembert Pareto (SVG natif, top 5 + agrégat « autres »).
 *
 * État vide (< 3 clients facturés sur la fenêtre, ou aucun CA positif) :
 *  - bannière masquée ;
 *  - tableau remplacé par une seule ligne « état vide » ;
 *  - camembert remplacé par un message « pas assez de données ».
 *
 * Convention CLAUDE.md respectée : pas de HTML inline en JS — tout est
 * construit via `document.createElement` + `textContent`. Le disclaimer
 * URSSAF est porté par un élément déjà présent dans le HTML statique
 * (le controller ne fait que basculer son `hidden`).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const EURO = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PERCENT = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const PALIER_LABELS = {
  info: "info",
  warning: "warning",
  critical: "critical",
};

const PALIER_PILL_CLASSES = {
  info: "nm-pill nm-pill--ochre",
  warning: "nm-pill nm-pill--red",
  critical: "nm-pill nm-pill--red",
};

const TOP_N = 5;

// Palette catégorielle (une couleur distincte par client) dérivée des tons
// Shibui de la charte. La plus grosse part (client le plus concentré, slices
// triées décroissant) reçoit l'accent indigo ; l'agrégat « autres » est neutre.
// On évite le rouge pur (réservé aux paliers warning/critical, signalés par les
// pastilles du tableau) : le risque se lit dans la bannière + les pills, pas
// dans la couleur d'une part de camembert.
const SLICE_PALETTE = [
  "var(--shibui-indigo)",
  "var(--shibui-blue)",
  "var(--shibui-green)",
  "var(--shibui-ochre)",
  "color-mix(in oklab, var(--shibui-red) 78%, var(--shibui-ochre))",
];
const OTHER_FILL = "var(--muted)";

export default class PilotageConcentrationController extends BaseController {
  static targets = [
    "alertBanner",
    "alertHeader",
    "alertMessage",
    "bncMention",
    "urssafDisclaimer",
    "tbody",
    "totalCaHt",
    "totalClients",
    "timelineList",
    "pareto",
  ];

  /** @type {Array<object>} */
  clients = [];

  async connect() {
    try {
      const payload = await api.getPilotageConcentration();
      this._hydrate(payload);
    } catch (error) {
      console.warn("[pilotage-concentration] hydration failed", error);
      this._renderEmpty();
    }
  }

  _hydrate(payload) {
    const clients = Array.isArray(payload?.clients) ? payload.clients : [];
    const totalCaHt = Number(payload?.totalCaHt) || 0;
    const maxPalier = typeof payload?.maxPalier === "string" ? payload.maxPalier : null;
    const hasBnc = Boolean(payload?.hasBnc);
    const recentEvents = Array.isArray(payload?.recentEvents) ? payload.recentEvents : [];
    const topClient = payload?.topClient ?? null;

    this.clients = clients;

    this._renderAlertBanner(maxPalier, topClient, hasBnc);
    this._renderTable(clients, totalCaHt);
    this._renderTimeline(recentEvents);
    this._renderPareto(clients, totalCaHt);
  }

  _renderEmpty() {
    this._renderAlertBanner(null, null, false);
    this._renderTable([], 0);
    this._renderTimeline([]);
    this._renderPareto([], 0);
  }

  _renderAlertBanner(maxPalier, topClient, hasBnc) {
    if (!this.hasAlertBannerTarget) return;

    if (maxPalier === null || maxPalier === "info" || topClient === null) {
      this.alertBannerTarget.hidden = true;
      return;
    }

    this.alertBannerTarget.hidden = false;

    if (this.hasAlertHeaderTarget) {
      this.alertHeaderTarget.textContent =
        `— Palier *${PALIER_LABELS[maxPalier] ?? maxPalier}* franchi `
        + (maxPalier === "critical" ? "(≥ 70 %)" : "(≥ 50 %)");
    }

    if (this.hasAlertMessageTarget) {
      this.alertMessageTarget.replaceChildren();
      const strong = document.createElement("strong");
      const pct = (Number(topClient.percent) || 0) * 100;
      strong.textContent =
        `${topClient.name ?? ""} représente ${PERCENT.format(pct)} % de votre CA`;
      this.alertMessageTarget.appendChild(strong);
      this.alertMessageTarget.appendChild(document.createTextNode(
        " sur les 12 derniers mois — surveillez votre dépendance commerciale.",
      ));
    }

    // Mention BNC affichée uniquement si activité BNC active + palier ≥ warning.
    if (this.hasBncMentionTarget) {
      this.bncMentionTarget.hidden = !(hasBnc && (maxPalier === "warning" || maxPalier === "critical"));
    }

    // Disclaimer URSSAF non-escamotable : visible dès le palier warning.
    if (this.hasUrssafDisclaimerTarget) {
      this.urssafDisclaimerTarget.hidden = !(maxPalier === "warning" || maxPalier === "critical");
    }
  }

  _renderTable(clients, totalCaHt) {
    if (!this.hasTbodyTarget) return;

    this.tbodyTarget.replaceChildren();

    if (clients.length === 0) {
      this.tbodyTarget.appendChild(this._buildEmptyRow());
      if (this.hasTotalCaHtTarget) this.totalCaHtTarget.textContent = "0,00 €";
      if (this.hasTotalClientsTarget) this.totalClientsTarget.textContent = "Pas assez de clients sur 12 mois";
      return;
    }

    const visible = clients.slice(0, TOP_N);
    const hidden = clients.slice(TOP_N);
    for (const client of visible) {
      this.tbodyTarget.appendChild(this._buildClientRow(client));
    }
    if (hidden.length > 0) {
      const sumHidden = hidden.reduce((s, c) => s + (Number(c.caHt) || 0), 0);
      const percentHidden = totalCaHt > 0 ? (sumHidden / totalCaHt) * 100 : 0;
      this.tbodyTarget.appendChild(this._buildOtherRow(hidden.length, sumHidden, percentHidden));
    }
    this.tbodyTarget.appendChild(this._buildTotalRow(totalCaHt, clients.length));

    if (this.hasTotalCaHtTarget) {
      this.totalCaHtTarget.textContent = `${EURO.format(totalCaHt)} €`;
    }
    if (this.hasTotalClientsTarget) {
      this.totalClientsTarget.textContent = `${clients.length} client${clients.length > 1 ? "s" : ""} sur 12 mois`;
    }
  }

  _renderTimeline(events) {
    if (!this.hasTimelineListTarget) return;
    this.timelineListTarget.replaceChildren();

    if (events.length === 0) {
      const li = document.createElement("li");
      const em = document.createElement("em");
      em.className = "text-subtle fs-13";
      em.style.fontStyle = "italic";
      em.style.fontFamily = "var(--serif)";
      em.textContent = "Aucun franchissement de palier sur les 12 derniers mois.";
      li.appendChild(em);
      this.timelineListTarget.appendChild(li);
      return;
    }

    for (const event of events.slice(0, 10)) {
      this.timelineListTarget.appendChild(this._buildTimelineRow(event));
    }
  }

  _renderPareto(clients, totalCaHt) {
    if (!this.hasParetoTarget) return;
    this.paretoTarget.replaceChildren();

    if (clients.length === 0 || totalCaHt <= 0) {
      const empty = document.createElement("div");
      empty.className = "text-subtle fs-13";
      empty.style.padding = "32px 16px";
      empty.style.textAlign = "center";
      empty.style.fontStyle = "italic";
      empty.style.fontFamily = "var(--serif)";
      empty.textContent = "Pas assez de données — affichage à partir de 3 clients facturés sur la fenêtre 12 mois.";
      this.paretoTarget.appendChild(empty);
      return;
    }

    const slices = this._computeSlices(clients, totalCaHt);
    const svg = this._buildSvgPieChart(slices);
    this.paretoTarget.appendChild(svg);

    const legend = this._buildLegend(slices);
    this.paretoTarget.appendChild(legend);
  }

  _buildClientRow(client) {
    const tr = document.createElement("tr");

    const tdClient = document.createElement("td");
    const strong = document.createElement("strong");
    const a = document.createElement("a");
    a.href = "/clients-detail.html";
    a.textContent = String(client.name ?? "");
    strong.appendChild(a);
    tdClient.appendChild(strong);
    tr.appendChild(tdClient);

    const caHt = Number(client.caHt) || 0;
    const tdCa = document.createElement("td");
    tdCa.className = "is-amount";
    tdCa.textContent = `${EURO.format(caHt)} €`;
    tr.appendChild(tdCa);

    const percentValue = (Number(client.percent) || 0) * 100;
    const palier = client.palier ?? null;
    const tdPercent = document.createElement("td");
    tdPercent.className = palier === "warning" || palier === "critical"
      ? "is-amount text-error"
      : "is-amount";
    tdPercent.style.fontWeight = "600";
    tdPercent.textContent = `${PERCENT.format(percentValue)} %`;
    tr.appendChild(tdPercent);

    // Score paiement : badge « mauvais payeur » issu du moteur EPIC-009
    // (batch anti-N+1 côté backend). Tiret neutre si false / données
    // insuffisantes (§03.43 — on n'affirme pas « bon payeur »).
    const tdScore = document.createElement("td");
    tdScore.appendChild(this._buildPaymentScoreCell(client));
    tr.appendChild(tdScore);

    const tdLevel = document.createElement("td");
    tdLevel.appendChild(this._buildPalierPill(palier));
    tr.appendChild(tdLevel);

    return tr;
  }

  _buildOtherRow(count, caHt, percent) {
    const tr = document.createElement("tr");

    const tdClient = document.createElement("td");
    const em = document.createElement("em");
    em.className = "text-muted fs-14";
    em.style.fontStyle = "italic";
    em.style.fontFamily = "var(--serif)";
    em.textContent = `+ ${count} autre${count > 1 ? "s" : ""} client${count > 1 ? "s" : ""}`;
    tdClient.appendChild(em);
    tr.appendChild(tdClient);

    const tdCa = document.createElement("td");
    tdCa.className = "is-amount text-muted";
    tdCa.textContent = `${EURO.format(caHt)} €`;
    tr.appendChild(tdCa);

    const tdPercent = document.createElement("td");
    tdPercent.className = "is-amount text-muted";
    tdPercent.textContent = `${PERCENT.format(percent)} %`;
    tr.appendChild(tdPercent);

    const tdScore = document.createElement("td");
    const scoreSpan = document.createElement("span");
    scoreSpan.className = "fs-12 text-subtle";
    scoreSpan.textContent = "—";
    tdScore.appendChild(scoreSpan);
    tr.appendChild(tdScore);

    const tdLevel = document.createElement("td");
    tdLevel.appendChild(this._buildPalierPill(null));
    tr.appendChild(tdLevel);

    return tr;
  }

  _buildTotalRow(totalCaHt, clientsCount) {
    const tr = document.createElement("tr");
    tr.style.background = "var(--surface)";

    const tdLabel = document.createElement("td");
    const strongLabel = document.createElement("strong");
    strongLabel.textContent = "Total CA HT";
    tdLabel.appendChild(strongLabel);
    tr.appendChild(tdLabel);

    const tdCa = document.createElement("td");
    tdCa.className = "is-amount";
    const strongCa = document.createElement("strong");
    strongCa.textContent = `${EURO.format(totalCaHt)} €`;
    tdCa.appendChild(strongCa);
    tr.appendChild(tdCa);

    const tdPercent = document.createElement("td");
    tdPercent.className = "is-amount";
    const strongPercent = document.createElement("strong");
    strongPercent.textContent = "100 %";
    tdPercent.appendChild(strongPercent);
    tr.appendChild(tdPercent);

    const tdMeta = document.createElement("td");
    tdMeta.colSpan = 2;
    const span = document.createElement("span");
    span.className = "fs-11 text-subtle";
    span.style.fontFamily = "var(--mono)";
    span.style.letterSpacing = "0.06em";
    span.style.textTransform = "uppercase";
    span.textContent = `${clientsCount} client${clientsCount > 1 ? "s" : ""} sur 12 mois`;
    tdMeta.appendChild(span);
    tr.appendChild(tdMeta);

    return tr;
  }

  _buildEmptyRow() {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = 5;
    td.className = "text-subtle";
    td.style.textAlign = "center";
    td.style.padding = "32px 12px";
    td.style.fontStyle = "italic";
    td.style.fontFamily = "var(--serif)";
    td.textContent = "Pas assez de clients facturés sur les 12 derniers mois — affichage actif à partir de 3 clients.";
    tr.appendChild(td);
    return tr;
  }

  _buildPalierPill(palier) {
    // Pas de palier (client sous le seuil info, ou ligne « + N autres ») →
    // pas de pastille, juste un tiret neutre.
    if (!palier || !PALIER_PILL_CLASSES[palier]) {
      const dash = document.createElement("span");
      dash.className = "fs-12 text-subtle";
      dash.textContent = "—";
      return dash;
    }

    const span = document.createElement("span");
    span.className = PALIER_PILL_CLASSES[palier];
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    span.appendChild(dot);
    span.appendChild(document.createTextNode(palier));
    return span;
  }

  _buildPaymentScoreCell(client) {
    // Badge « mauvais payeur » aligné sur la liste clients (clients.html,
    // tpl-client-row) : même pastille `nm-pill--red` + même libellé. Tiret
    // neutre si false ou données insuffisantes — on n'affirme jamais un
    // « bon payeur » (§03.43).
    if (client.paymentScore?.badPayerBadge === true) {
      const pill = document.createElement("span");
      pill.className = "nm-pill nm-pill--red";
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      dot.setAttribute("aria-hidden", "true");
      pill.appendChild(dot);
      pill.appendChild(document.createTextNode("mauvais payeur"));
      return pill;
    }

    const dash = document.createElement("span");
    dash.className = "fs-12 text-subtle";
    dash.textContent = "—";
    return dash;
  }

  _buildTimelineRow(event) {
    const li = document.createElement("li");

    const when = document.createElement("span");
    when.className = "nm-timeline__when";
    when.textContent = this._formatShortDate(event.detectedAt);
    li.appendChild(when);

    const div = document.createElement("div");
    const what = document.createElement("span");
    what.className = "nm-timeline__what";
    const data = event.data ?? {};
    const clientName = String(data.clientName ?? "Un client");
    const palier = String(data.palier ?? "");
    const direction = String(event.direction ?? "up");
    if (direction === "down") {
      what.textContent = `${clientName} repasse sous le palier *${palier}*`;
    } else {
      const seuilPct = palier === "info" ? "30 %"
        : palier === "warning" ? "50 %"
        : palier === "critical" ? "70 %"
        : "";
      what.textContent = `${clientName} franchit le palier *${palier}*${seuilPct !== "" ? ` (${seuilPct})` : ""}`;
    }
    div.appendChild(what);

    const who = document.createElement("em");
    who.className = "nm-timeline__who";
    const percentValue = (Number(data.percent) || 0) * 100;
    who.textContent = `Concentration ${PERCENT.format(percentValue)} %`;
    div.appendChild(who);

    li.appendChild(div);
    return li;
  }

  _computeSlices(clients, totalCaHt) {
    const top = clients.slice(0, TOP_N);
    const rest = clients.slice(TOP_N);
    const slices = top.map((client) => ({
      name: String(client.name ?? ""),
      caHt: Number(client.caHt) || 0,
      percent: ((Number(client.caHt) || 0) / totalCaHt) * 100,
      palier: client.palier ?? null,
    }));
    if (rest.length > 0) {
      const sum = rest.reduce((s, c) => s + (Number(c.caHt) || 0), 0);
      slices.push({
        name: `+ ${rest.length} autre${rest.length > 1 ? "s" : ""}`,
        caHt: sum,
        percent: (sum / totalCaHt) * 100,
        palier: null,
        isOther: true,
      });
    }
    return slices;
  }

  _buildSvgPieChart(slices) {
    const size = 280;
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 8;

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    svg.setAttribute("width", "100%");
    svg.setAttribute("style", "max-width: 320px; aspect-ratio: 1;");
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", "Répartition Pareto du chiffre d'affaires par client");

    let startAngle = -Math.PI / 2;
    const totalPct = slices.reduce((s, slice) => s + slice.percent, 0);
    slices.forEach((slice, index) => {
      const angle = (slice.percent / Math.max(totalPct, 0.0001)) * Math.PI * 2;
      const endAngle = startAngle + angle;
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const d = this._describeArc(cx, cy, r, startAngle, endAngle);
      path.setAttribute("d", d);
      path.setAttribute("fill", this._sliceColor(slice, index));
      path.setAttribute("stroke", "var(--surface)");
      path.setAttribute("stroke-width", "2");
      const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
      title.textContent = `${slice.name} — ${PERCENT.format(slice.percent)} %`;
      path.appendChild(title);
      svg.appendChild(path);
      startAngle = endAngle;
    });
    return svg;
  }

  _describeArc(cx, cy, r, start, end) {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = end - start > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  _sliceColor(slice, index) {
    if (slice.isOther) return OTHER_FILL;
    return SLICE_PALETTE[index % SLICE_PALETTE.length];
  }

  _buildLegend(slices) {
    const legend = document.createElement("div");
    legend.className = "nm-ph__cap";
    legend.style.marginTop = "12px";
    const ul = document.createElement("ul");
    ul.style.listStyle = "none";
    ul.style.padding = "0";
    ul.style.margin = "0";
    ul.style.display = "flex";
    ul.style.flexDirection = "column";
    ul.style.gap = "6px";
    slices.forEach((slice, index) => {
      const li = document.createElement("li");
      li.style.display = "flex";
      li.style.alignItems = "center";
      li.style.gap = "8px";
      li.style.fontSize = "13px";
      const swatch = document.createElement("span");
      swatch.style.width = "10px";
      swatch.style.height = "10px";
      swatch.style.borderRadius = "2px";
      swatch.style.background = this._sliceColor(slice, index);
      li.appendChild(swatch);
      const label = document.createElement("span");
      label.textContent = `${slice.name} — ${PERCENT.format(slice.percent)} %`;
      li.appendChild(label);
      ul.appendChild(li);
    });
    legend.appendChild(ul);
    return legend;
  }

  _formatShortDate(iso) {
    if (typeof iso !== "string" || iso === "") return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
      }).format(d).replace(".", "");
    } catch {
      return "—";
    }
  }
}
